import { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Chip,
} from "@mui/material";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { handleApplyFilterItemFunction } from "./FiltersContainer";
import {
  IOptionSchema,
  IOptionTableSchema,
  OperatorEnum,
} from "../../../../utils/types";

interface SelectionFilterProps {
  handleApplyFilter: handleApplyFilterItemFunction;
  value: string | string[] | null | undefined;
  operator: string;
  options?: IOptionTableSchema | null;
  columnName: string;
}

const operatorOptions: IOptionSchema[] = [
  { value: OperatorEnum.IS_EQUAL, label: "مساوی" },
  { value: OperatorEnum.IN, label: "در" },
  { value: OperatorEnum.NOT_IN, label: "نباشد در" },
  { value: OperatorEnum.IN_OR_NULL, label: "در یا خالی" },
  { value: OperatorEnum.IS_NULL, label: "خالی باشد" },
];

const convertObjectToSelectOptions = (
  obj?: IOptionTableSchema | null
): IOptionSchema[] => {
  if (!obj || typeof obj !== "object") return [];
  return Object.entries(obj).map(([value, label]) => ({
    value: String(value),
    label: String(label),
  }));
};

const SelectionFilter = ({
  handleApplyFilter,
  value,
  operator: operatorValue,
  options,
  columnName,
}: SelectionFilterProps) => {
  const selectOptions = useMemo(() => {
    const opts = convertObjectToSelectOptions(options);
    return opts.length > 0
      ? opts
      : [{ value: "", label: "هیچ گزینه‌ای موجود نیست" }];
  }, [options]);

  const [selectedOption, setSelectedOption] = useState<string[]>([]);
  const [operator, setOperator] = useState<string>(
    operatorValue || OperatorEnum.IS_EQUAL
  );

  // ✅ مقداردهی اولیه براساس props.value
  useEffect(() => {
    if (!value) {
      setSelectedOption([]);
      return;
    }

    if (Array.isArray(value)) {
      setSelectedOption(value.map(String));
    } else {
      setSelectedOption([String(value)]);
    }
  }, [value]);

  // ✅ فقط اپراتورهای خاص چند‌مقداری باشند
  const multiValueOperators = [
    OperatorEnum.IN,
    OperatorEnum.NOT_IN,
    OperatorEnum.IN_OR_NULL,
  ];

  useEffect(() => {
    if (!multiValueOperators.includes(operator as any) && selectedOption.length > 1) {
      setSelectedOption([selectedOption[0]]);
    }
  }, [operator]);

  const applyHandler = () => {
    handleApplyFilter(
      columnName,
      operator,
      multiValueOperators.includes(operator as any)
        ? selectedOption
        : selectedOption[0]
    );
  };
  const selectOperatorHandler = (event: SelectChangeEvent<string>) => {
    setOperator(event.target.value);
  };

  const selectOptionHandler = (event: SelectChangeEvent<string[]>) => {
    const val = event.target.value;
    setSelectedOption(typeof val === "string" ? val.split(",") : val);
  };

  const hideValueInput = operator === OperatorEnum.IS_NULL;
  
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <FormControl fullWidth size="small" dir="rtl">
        <InputLabel id="operator-label">عملگر</InputLabel>
        <Select
          labelId="operator-label"
          value={operator}
          label="عملگر"
          onChange={selectOperatorHandler}
          MenuProps={{
            PaperProps: { sx: { textAlign: "right", direction: "rtl" } },
          }}
        >
          {operatorOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {!hideValueInput && (
        <Box display="flex" alignItems="center" gap={2}>
          <ArrowForward sx={{ transform: "rotate(180deg)", color: "gray" }} />
          <FormControl fullWidth size="small" dir="rtl">
            <InputLabel id="value-label">مقدار</InputLabel>
            <Select
              labelId="value-label"
              value={selectedOption}
              label="مقدار"
              onChange={selectOptionHandler}
              multiple={multiValueOperators.includes(operator as any)}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected as string[]).map((val) => {
                    const label =
                      selectOptions.find((o) => o.value === val)?.label || val;
                    return <Chip key={val} label={label} size="small" />;
                  })}
                </Box>
              )}
              MenuProps={{
                PaperProps: { sx: { textAlign: "right", direction: "rtl" } },
              }}
            >
              {selectOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* دکمه تایید */}
      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={applyHandler}
        sx={{ alignSelf: "flex-start", height: 36 }}
      >
        تایید
      </Button>
    </Box>
  );
};

export default SelectionFilter;
